import { useAuth } from '@clerk/clerk-react';
import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useCallback } from 'react';

import { useZero } from '@/lib/zero';
import { useCurrentOrg } from '@/services/org';

export function useLayoutPreferences(layoutType: string) {
  const { userId } = useAuth();
  const z = useZero();
  const { currentOrg } = useCurrentOrg();
  
  // Query all layout preferences for this type and organization
  const [preferences] = useZeroQuery(
    z.query.UserPreferencesTable
      .where(({ cmp, and }) => 
        and(
          cmp('userId', userId ?? ''),
          cmp('organizationId', currentOrg.account.login),
          cmp('layoutType', layoutType)
        )
      )
  );
  

  // Get a specific layout preference value
  const getLayoutPreference = useCallback((key: string) => {
    return preferences?.find(p => p.layoutKey === key)?.layoutValue;
  }, [preferences]);
  
  // Set a layout preference (creates or updates)
  const setLayoutPreference = useCallback(async (key: string, value: string) => {
    const existing = preferences?.find(p => p.layoutKey === key);
    
    await z.mutate.UserPreferencesTable.upsert({
      id: existing?.id || crypto.randomUUID(),
      userId: userId!,
      organizationId: currentOrg.account.login,
      layoutType,
      layoutKey: key,
      layoutValue: value,
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }, [z, userId, currentOrg, preferences, layoutType]);
  
  // Delete a layout preference
  const deleteLayoutPreference = useCallback(async (key: string) => {
    const existing = preferences?.find(p => p.layoutKey === key);
    if (existing) {
      await z.mutate.UserPreferencesTable.delete({ id: existing.id });
    }
  }, [z, preferences]);
  
  // Check if preferences have loaded (not null/undefined)
  const isLoading = preferences === undefined;
  
  return { getLayoutPreference, setLayoutPreference, deleteLayoutPreference, preferences, isLoading };
}